<template>
  <div class="minister-unassigned">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="serverError" />

    <div class="minister-unassigned__header">
      <div class="header-left">
        <button @click="$router.push({ name: dashboardRouteName })" class="back-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="back-btn__icon"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          Back to Dashboard
        </button>
        <div class="minister-unassigned__title-group">
          <div class="minister-unassigned__eyebrow">Data Clean-up & Audit</div>
          <h2 class="minister-unassigned__title">Unassigned & Unmapped Incidents</h2>
        </div>
      </div>
      <div class="header-right">
        <div class="incidents-counter">
          <span class="incidents-counter__label">Total Unassigned</span>
          <span class="incidents-counter__value">{{ filteredIncidents.length }}</span>
        </div>
      </div>
    </div>

    <div class="minister-unassigned__container">
      <!-- Info Alert Card -->
      <div class="info-alert-card">
        <div class="info-alert-card__icon">⚠️</div>
        <div class="info-alert-card__text">
          This page lists incidents from yesterday that are missing a mapped District (marked <strong>Unknown</strong>) or still fall outside an operational territorial division. Use this audit screen to verify coordinate precision or trigger manual administrative assignment.
        </div>
      </div>

      <!-- Table Card -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="incidents-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Animal Name</th>
                <th>District</th>
                <th>Division</th>
                <th>GPS Coordinates</th>
                <th>Reported By</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="incident in paginatedIncidents" :key="incident.id">
                <td>
                  <span :class="['type-badge', `type-badge--${incident.type.toLowerCase()}`]">
                    {{ incident.type }}
                  </span>
                </td>
                <td class="cell-primary">{{ incident.animalName }}</td>
                <td>
                  <span :class="{'text-unknown': incident.district === 'Unknown', 'text-omitted': incident.district === 'Outside Kerala'}">
                    {{ incident.district }}
                  </span>
                </td>
                <td>
                  <span :class="{'text-unknown': incident.division === '-'}">
                    {{ incident.division }}
                  </span>
                </td>
                <td>
                  <span v-if="incident.location && incident.location.length >= 2" class="gps-coords">
                    {{ formatGPS(incident.location) }}
                  </span>
                  <span v-else class="text-unknown">
                    -
                  </span>
                </td>
                <td>{{ incident.reportedBy }}</td>
                <td>{{ formatDate(incident.date) }}</td>
                <td>
                  <span :class="['status-badge', `status-badge--${getStatusClass(incident.status)}`]">
                    {{ incident.status }}
                  </span>
                </td>
              </tr>
              <tr v-if="filteredIncidents.length === 0">
                <td colspan="8" class="empty-state">
                  <div class="empty-state__icon">🎉</div>
                  <div class="empty-state__text">All incidents are correctly mapped and assigned!</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination" v-if="filteredIncidents.length > 0">
          <div class="pagination__info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, filteredIncidents.length) }} of {{ filteredIncidents.length }} unassigned incidents
          </div>
          <div class="pagination__controls">
            <button
              :disabled="currentPage === 1"
              @click="currentPage--"
              class="pagination-btn"
            >
              Previous
            </button>
            <button
              v-for="page in totalPages"
              :key="page"
              :class="['pagination-btn', { 'pagination-btn--active': currentPage === page }]"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
            <button
              :disabled="currentPage === totalPages"
              @click="currentPage++"
              class="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      serverError: false,
      incidents: [],
      selectedDate: "",
      currentPage: 1,
      itemsPerPage: 10,
      resolvedStatuses: new Set([
        "resolved",
        "closed",
        "captured",
        "rescued",
        "released",
        "handover",
        "approved"
      ])
    };
  },
  computed: {
    dashboardRouteName() {
      return this.$route.name === "adminCommonUnassigned"
        ? "commonanalyticspage"
        : "ministerDashboard";
    },
    filteredIncidents() {
      return this.incidents;
    },
    totalPages() {
      return Math.ceil(this.filteredIncidents.length / this.itemsPerPage) || 1;
    },
    paginatedIncidents() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredIncidents.slice(start, end);
    }
  },
  mounted() {
    this.syncFiltersFromRoute();
    this.loadIncidents();
  },
  methods: {
    syncFiltersFromRoute() {
      this.selectedDate = typeof this.$route.query.date === "string"
        ? this.$route.query.date
        : "";
    },
    async loadIncidents() {
      this.appLoading = true;
      this.serverError = false;
      try {
        const listRes = await axios({
          method: "GET",
          url: "/minister/dashboard/unassigned",
          headers: { "x-auth-token": localStorage.getItem("token") },
          params: {
            date: this.selectedDate || undefined,
          },
        });
        this.incidents = listRes.data.data || [];
      } catch (error) {
        console.error(error);
        this.serverError = true;
      } finally {
        this.appLoading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      });
    },
    formatGPS(location) {
      if (!location || !Array.isArray(location) || location.length < 2) return "-";
      const lat = Number(location[0]);
      const lng = Number(location[1]);
      if (isNaN(lat) || isNaN(lng)) return "-";
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    },
    getStatusClass(status) {
      if (!status) return "pending";
      const statusLower = status.toLowerCase();
      if (this.resolvedStatuses.has(statusLower)) {
        return "resolved";
      }
      if (statusLower === "pending" || statusLower === "assigned" || statusLower === "onreview") {
        return "pending";
      }
      return "neutral";
    }
  }
};
</script>

<style scoped>
.minister-unassigned {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  padding: 4px;
}

.minister-unassigned__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.12);
  color: #2e5a38;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(18, 42, 24, 0.05);
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f4f8f3;
  border-color: rgba(34, 64, 35, 0.24);
  transform: translateX(-2px);
}

.back-btn__icon {
  color: #2e5a38;
}

.minister-unassigned__title-group {
  display: flex;
  flex-direction: column;
}

.minister-unassigned__eyebrow {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #697b69;
  font-weight: 700;
}

.minister-unassigned__title {
  margin-top: 2px;
  font-size: 20px;
  line-height: 1.1;
  color: #183824;
  font-weight: 700;
}

.incidents-counter {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.08);
  box-shadow: 0 4px 12px rgba(18, 42, 24, 0.05);
}

.incidents-counter__label {
  font-size: 13px;
  color: #6c7c6c;
  font-weight: 500;
}

.incidents-counter__value {
  font-size: 18px;
  font-weight: 800;
  color: #c62828;
}

.minister-unassigned__container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-alert-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 16px;
  padding: 14px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
}

.info-alert-card__icon {
  font-size: 18px;
}

.info-alert-card__text {
  font-size: 13px;
  color: #5d4037;
  line-height: 1.5;
}

.table-card {
  flex: 1;
  min-height: 0;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(34, 64, 35, 0.08);
  border-radius: 20px;
  box-shadow: 0 16px 28px rgba(18, 42, 24, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-wrapper {
  flex: 1;
  overflow-y: auto;
}

.incidents-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.incidents-table th {
  position: sticky;
  top: 0;
  background: #f4f8f3;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #4a5c4d;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(34, 64, 35, 0.08);
  z-index: 1;
}

.incidents-table td {
  padding: 16px 20px;
  font-size: 14px;
  color: #384c3c;
  border-bottom: 1px solid rgba(34, 64, 35, 0.05);
}

.incidents-table tbody tr {
  transition: background-color 0.15s ease;
}

.incidents-table tbody tr:hover {
  background-color: rgba(46, 90, 56, 0.03);
}

.cell-primary {
  font-weight: 700;
  color: #173824;
}

.text-unknown {
  color: #c62828 !important;
  font-weight: 700;
  background: rgba(239, 83, 80, 0.08);
  padding: 2px 8px;
  border-radius: 6px;
}

.text-omitted {
  color: #e65100 !important;
  font-weight: 700;
  background: rgba(255, 171, 118, 0.12);
  padding: 2px 8px;
  border-radius: 6px;
}

.gps-coords {
  font-family: monospace;
  font-size: 12px;
  color: #4a5c4d;
  background: rgba(34, 64, 35, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
}

.type-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.type-badge--hwc {
  background: #e2f0d9;
  color: #385624;
}

.type-badge--snake {
  background: #e2eff9;
  color: #205c90;
}

.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
}

.status-badge--resolved {
  background: #e2f5e4;
  color: #2e6b36;
}

.status-badge--pending {
  background: #fff0db;
  color: #a36518;
}

.status-badge--neutral {
  background: #f0f0f0;
  color: #555555;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state__icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-state__text {
  font-size: 15px;
  color: #718171;
  font-weight: 500;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid rgba(34, 64, 35, 0.08);
  background: #ffffff;
}

.pagination__info {
  font-size: 13px;
  color: #6c7c6c;
}

.pagination__controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-btn {
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(34, 64, 35, 0.1);
  background: #ffffff;
  color: #2e5a38;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f4f8f3;
  border-color: rgba(34, 64, 35, 0.2);
}

.pagination-btn--active {
  background: #2e5a38;
  border-color: #2e5a38;
  color: #ffffff;
  cursor: default;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #8c9c8c;
}
</style>
